import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Bell, RefreshCw, CheckCircle2, AlertOctagon } from 'lucide-react';
import { toast } from 'sonner';

export default function AlertsPage() {
  const { authAxios } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);

  const fetchData = async () => {
    try {
      const api = authAxios();
      const res = await api.get('/alerts/latest');
      setAlerts(res.data);
    } catch (err) {
      toast.error('Failed to fetch alerts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleRun = async () => {
    setTriggering(true);
    try {
      const api = authAxios();
      const res = await api.post('/alerts/run');
      toast.success(`Alerts run complete. Sent: ${res.data.alerts_sent}`);
      await fetchData();
    } catch (err) {
      toast.error('Failed to trigger alerts');
    } finally {
      setTriggering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="w-8 h-8 text-[#EF694C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-white tracking-tight">
            Alerts
          </h1>
          <p className="text-sm text-[#a1a1aa] mt-1">
            Latest system notifications
          </p>
        </div>
        <Button
          onClick={handleRun}
          disabled={triggering}
          className="bg-[#EF694C] hover:bg-[#d65d43] text-white"
        >
          <Bell className="w-4 h-4 mr-2" />
          {triggering ? 'Running...' : 'Run Checks'}
        </Button>
      </div>

      <div className="space-y-4">
        {alerts.map((alert, i) => (
          <Card key={i} className="bg-[#121214] border-white/10 hover:border-white/20 transition-colors">
            <CardContent className="p-4 flex items-start gap-4">
              <div className={`p-2 rounded-lg bg-white/5 ${
                alert.severity === 'critical' ? 'text-red-500' : 
                alert.severity === 'high' ? 'text-amber-500' : 'text-blue-500'
              }`}>
                <AlertOctagon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-white">{alert.type}</h3>
                  <span className="text-xs text-[#52525b]">
                    {new Date(alert.ts).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm text-[#a1a1aa] mt-1">{alert.payload}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="border-white/10 text-[#52525b] text-[10px]">
                    {alert.entity}
                  </Badge>
                  {alert.delivered && (
                    <Badge className="bg-emerald-500/10 text-emerald-500 text-[10px]">
                      Delivered
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {alerts.length === 0 && (
          <div className="text-center py-12 text-[#52525b]">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p>No recent alerts</p>
          </div>
        )}
      </div>
    </div>
  );
}
